import { createSelector } from 'reselect'
import { emailRegex } from 'utilities/formsyValidators'
import { OrderFeedbackReplyAddressState } from './types'

export const selectOrderFeedbackReplyAddressState = (state: {
  orderFeedbackReplyAddress: OrderFeedbackReplyAddressState
}) => state.orderFeedbackReplyAddress

// `selectFormDisabled` gates the pane's CONTROLS, and "a save is in flight" is
// the only reason to disable those. Publish has further conditions of its
// own, see `selectPublishDisabled`.
export const selectFormDisabled = createSelector(
  [selectOrderFeedbackReplyAddressState],
  state => state.isFormSubmitting
)

export const selectReplyMode = createSelector(
  [selectOrderFeedbackReplyAddressState],
  state => state.replyMode
)

export const selectReplyAddress = createSelector(
  [selectOrderFeedbackReplyAddressState],
  state => state.replyAddress
)

// The last mode/address known to be SAVED, as opposed to the pair being
// edited. No consumer needs these yet (there is no "current setup" card on
// this pane), but they complete the pair the way
// selectPersistedAllowWrittenFeedbackCollection does for the sibling slice.
export const selectPersistedReplyMode = createSelector(
  [selectOrderFeedbackReplyAddressState],
  state => state.persistedReplyMode
)

export const selectPersistedReplyAddress = createSelector(
  [selectOrderFeedbackReplyAddressState],
  state => state.persistedReplyAddress
)

export const selectIsFormSubmitting = createSelector(
  [selectOrderFeedbackReplyAddressState],
  state => state.isFormSubmitting
)

export type AddressError = 'required' | 'invalid' | 'thanx_blocked' | null

// A reply address must never point back into Thanx's own mail domain — the
// same rule scenes/Cms/Settings/EmailReplyRouting/helpers.ts enforces for its
// own reply-routing addresses. That constant is private to that file (not
// exported), so the same pattern is redefined here rather than imported.
const THANX_ADDRESS = /@(?:[a-z0-9-]+\.)*thanx\.com$/i

// Only the 'fixed' mode has an address to validate — 'user' mode has nothing
// of its own that can be wrong.
export const selectAddressError = createSelector(
  [selectReplyMode, selectReplyAddress],
  (replyMode, replyAddress): AddressError => {
    if (replyMode !== 'fixed') return null
    const trimmed = replyAddress.trim()
    if (!trimmed) return 'required'
    if (!emailRegex.test(trimmed)) return 'invalid'
    if (THANX_ADDRESS.test(trimmed)) return 'thanx_blocked'
    return null
  }
)

// True when either edited value differs from its persisted counterpart.
export const selectIsFormDirty = createSelector(
  [selectOrderFeedbackReplyAddressState],
  state =>
    state.replyMode !== state.persistedReplyMode ||
    state.replyAddress !== state.persistedReplyAddress
)

// Publish is disabled while a save is in flight, whenever there is nothing to
// save, OR while the fixed-mode address fails validation — a merchant must
// not be able to publish an address guests' replies cannot reach.
export const selectPublishDisabled = createSelector(
  [selectFormDisabled, selectIsFormDirty, selectAddressError],
  (formDisabled, isFormDirty, addressError) =>
    formDisabled || !isFormDirty || !!addressError
)

// The PATCH body carries exactly these two keys. 'fixed' mode sends the
// trimmed address; 'user' mode sends null so a stale address never lingers on
// the merchant record once the mode switches away from it.
export const selectApiFormattedFormData = createSelector(
  [selectOrderFeedbackReplyAddressState],
  state => ({
    order_feedback_reply_mode: state.replyMode,
    order_feedback_reply_address:
      state.replyMode === 'fixed' ? state.replyAddress.trim() : null,
  })
)
